import { prop, propEq, propOr } from 'ramda';

import { compact, date, eq, expandIf } from '../../../helpers/compact';
import { DOC_CLASS_CATEGORIES } from '../../../helpers/pushs';
import { DocumentType } from '../../../types/document';
import { CLASSIFICATION, FILTER, PUBLICATION } from '../../../types/filters';
import { MEDIA_TYPE } from '../../../types/press-media';
import { toLinkFromProp } from '../fields/link';
import { toTextFromProp } from '../fields/text';
import {
  regionTypeDefault,
  toMediaPrismic,
  toMediaThematicType,
  toMediaType,
  toPublicationType,
  toRegionType,
  toThematicType,
} from '../mapping';
import { image } from '../partials/image';
import { deprecatedPageEntity } from './internal';

const getDownloadUrl = (normalizedImage, normalizedLink) =>
  propOr(prop('src', normalizedImage), 'url', normalizedLink);

const isMediaOfType = (mediaType, data) =>
  propEq('category', toMediaPrismic(mediaType), data);

const toFinancialPublication = (publicationType, data) => {
  if (isMediaOfType(MEDIA_TYPE.RELEASE, data)) {
    return PUBLICATION.PRESS_RELEASE;
  }

  return toPublicationType(publicationType);
};

const processMediaPress = (data) => {
  const normalizedImage = image(data);
  const normalizedLink = toLinkFromProp('uri', data);
  const onFinance =
    (isMediaOfType(MEDIA_TYPE.RELEASE, data) &&
      eq('com_visible_finance', 'oui', false, data)) ||
    (isMediaOfType(MEDIA_TYPE.PUBLICATION, data) &&
      eq('pub_visible_finance', 'oui', true, data));
  const publicationType = prop('publication', data) || prop('pub_type', data);

  return compact({
    type: DocumentType.MEDIA_PRESS,
    description: toTextFromProp('description', data),
    publication: toPublicationType(publicationType),
    mediaType: toMediaType(prop('category', data)),
    thematic: toThematicType(prop('thematic', data)),
    region:
      toRegionType(prop('national', data)) ||
      toRegionType(prop('international', data)),
    image: normalizedImage,
    link: normalizedLink,
    downloadUrl: getDownloadUrl(normalizedImage, normalizedLink),
    related_article: toLinkFromProp('related_article', data),
    onNewsroom: true,
    ...expandIf(isMediaOfType(MEDIA_TYPE.RELEASE, data), () => ({
      onNewsroom: eq('com_visible_newsroom', 'oui', true, data),
      onFinance,
      relatedDate: date(prop('com_related_date', data), 'YYYY-MM-DD'),
      date: date(prop('date', data), 'YYYY-MM-DD'),
      createdAt: date(prop('date', data), 'YYYY-MM-DD'),
    })),
    ...expandIf(isMediaOfType(MEDIA_TYPE.VIDEO, data), () => ({
      transcription: prop('video_transcription', data),
    })),
    ...expandIf(isMediaOfType(MEDIA_TYPE.AUDIO, data), () => ({
      transcription: prop('audio_transcription', data),
    })),
    ...expandIf(isMediaOfType(MEDIA_TYPE.PUBLICATION, data), () => ({
      onNewsroom: false,
      classCategory: DOC_CLASS_CATEGORIES.PUBLICATION,
      onHome: eq('pub_visible_home', 'oui', true, data),
      onFinance,
      date: date(prop('pub_date', data), 'YYYY-MM-DD'),
      relatedDate: date(prop('pub_related_date', data), 'YYYY-MM-DD'),
      // publicationType: prop('pub_type', data),
    })),
    // in filters, there might be redundant data but it's necessary for the search engine.
    filters: compact({
      [FILTER.MEDIA_TYPE]: toMediaType(prop('category', data)),
      [FILTER.REGION]:
        toRegionType(prop('national', data)) ||
        toRegionType(prop('international', data)) ||
        regionTypeDefault(),
      [FILTER.THEMATIC]: toThematicType(prop('thematic', data)),
      [FILTER.MEDIA_THEMATIC]: toMediaThematicType(prop('mediaThematic', data)),
      [FILTER.COMMON_THEMATIC]:
        toMediaThematicType(prop('mediaThematic', data)) ||
        toThematicType(prop('thematic', data)),
      [FILTER.CLASSIFICATION]: isMediaOfType(MEDIA_TYPE.PUBLICATION, data)
        ? CLASSIFICATION.PUBLICATION
        : null,
      [FILTER.PUBLICATION]:
        !onFinance && isMediaOfType(MEDIA_TYPE.PUBLICATION, data)
          ? toPublicationType(publicationType)
          : null,
      [FILTER.FINANCIAL_PUBLICATION]: onFinance
        ? toFinancialPublication(publicationType, data)
        : null,
    }),
  });
};

export const MediaPress = deprecatedPageEntity(DocumentType.MEDIA_PRESS, {
  processStrategy: (entity) => ({
    ...processMediaPress(prop('data', entity)),
  }),
});
